import React, { useState, useEffect, useRef, useMemo } from 'react';
import './RawData.css';
import PortalHeader from '../../../client-dashboard/clientNavbar/PortalHeader';
import {
  IoFolderOpen,
  IoDocumentText,
  IoBarChart,
  IoDownload,
} from 'react-icons/io5';

import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { TbZoomReset, TbZoomIn, TbZoomOut } from 'react-icons/tb';
import { ChartOptions } from 'chart.js';

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin
);

interface FileEntry {
  is_dir: boolean;
  name: string;
  path: string; // full path from root
  children?: FileEntry[];
}

interface FileInfo {
  date: string;
  receiver: string;
  location: { latitude: number; longitude: number };
}

const RawData = () => {
  const [directoryTree, setDirectoryTree] = useState<FileEntry[]>([]);
  const [selectedFile, setSelectedFile] = useState<FileEntry | null>(null);
  const [loadingDirs, setLoadingDirs] = useState<Set<string>>(new Set());
  const [expandedDirs, setExpandedDirs] = useState<Set<string>>(new Set());
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Plot related state
  const [plotData, setPlotData] = useState<{ x: number[]; y: number[] } | null>(
    null
  );
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
  const [isPlotVisible, setIsPlotVisible] = useState(false);
  const chartRef = useRef<any>(null);

  // To change the max variables in x-axis
  const [isNarrowScreen, setIsNarrowScreen] = useState(window.innerWidth < 900);
  useEffect(() => {
    const handleResize = () => setIsNarrowScreen(window.innerWidth < 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // To show or not the hint text
  const [isWideScreen, setIsWideScreen] = useState(window.innerWidth > 600);
  useEffect(() => {
    const handleResize = () => setIsWideScreen(window.innerWidth > 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const truncateFileName = (name: string, maxLength = 20) => {
    if (name.length <= maxLength) return name;
    const extIndex = name.lastIndexOf('.');
    const ext = extIndex !== -1 ? name.slice(extIndex) : '';
    const base = extIndex !== -1 ? name.slice(0, extIndex) : name;
    return base.slice(0, maxLength - ext.length - 3) + '...' + ext;
  };

  useEffect(() => {
    fetchDirectory('');
  }, []);

  // ---------- Directory fetching logic ----------
  const fetchDirectory = async (path: string) => {
    setLoadingDirs((prev) => new Set(prev).add(path));
    try {
      const response = await fetch(
        `/portal/api/croatia/list?path=${encodeURIComponent(path)}`,
        {
          method: 'GET',
          credentials: 'same-origin',
        }
      );
      if (response.ok) {
        const data = await response.json();
        const entries: FileEntry[] = data.entries.map((entry: any) => ({
          ...entry,
          path: path ? `${path}/${entry.name}` : entry.name,
          children: entry.is_dir ? [] : undefined,
        }));
        if (!path) setDirectoryTree(entries);
        else
          setDirectoryTree((prev) =>
            updateTreeWithChildren(prev, path, entries)
          );
      } else console.error('Failed to fetch directory');
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDirs((prev) => {
        const next = new Set(prev);
        next.delete(path);
        return next;
      });
    }
  };

  const updateTreeWithChildren = (
    tree: FileEntry[],
    targetPath: string,
    children: FileEntry[]
  ): FileEntry[] => {
    return tree.map((node) => {
      if (node.is_dir && node.path === targetPath) return { ...node, children };
      else if (node.children)
        return {
          ...node,
          children: updateTreeWithChildren(node.children, targetPath, children),
        };
      return node;
    });
  };

  const handleFileClick = (file: FileEntry) => {
    if (!file.is_dir) {
      setSelectedFile(file);
      setPlotData(null); // clear old plot
      setFileInfo(null);
      setIsPlotVisible(false); // ⬅️ do NOT plot on select
    } else {
      setExpandedDirs((prev) => {
        const next = new Set(prev);
        if (next.has(file.path)) next.delete(file.path);
        else {
          next.add(file.path);
          if (file.children && file.children.length === 0)
            fetchDirectory(file.path);
        }
        return next;
      });
    }
  };

  const handleDownload = (file: FileEntry) => {
    const downloadUrl = `/portal/api/croatia/download?path=${encodeURIComponent(
      file.path
    )}`;
    window.open(downloadUrl, '_blank');
  };

  // ---------- Plot logic ----------
  const chartOptions = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          title: { display: true, text: 'Time (s)' },
          ticks: {
            autoSkip: true,
            maxTicksLimit: isNarrowScreen ? 5 : 10, // ✅ dynamic based on screen
            maxRotation: 0,
            minRotation: 0,
          },
        },
        y: {
          title: { display: true, text: 'ADC' },
        },
      },
      plugins: {
        zoom: {
          pan: { enabled: true, mode: 'x', modifierKey: 'ctrl' },
          zoom: {
            wheel: { enabled: false },
            pinch: { enabled: false },
            drag: {
              enabled: true,
              borderColor: 'rgba(40, 170, 225, 0.5)',
              borderWidth: 1,
              backgroundColor: 'rgba(40, 170, 225, 0.15)',
            },
            mode: 'xy',
          },
        },
      },
    }),
    [isNarrowScreen]
  );

  const resetZoom = () => chartRef.current?.resetZoom();
  const zoomIn = () => chartRef.current?.zoom(1.2);
  const zoomOut = () => chartRef.current?.zoom(0.8);

  // ---- HEADER PARSING (copied from Plot.tsx, trimmed) ----
  const parseHeader = (headerString: string) => {
    const header = headerString.split(',');

    try {
      if (header.length <= 5) {
        const [MAC, LAT, LONG] = header;
        return {
          MAC,
          LAT: parseInt(LAT),
          LONG: parseInt(LONG),
        };
      } else {
        const [MAC, LAT, LONG] = header;
        return {
          MAC,
          LAT: parseInt(LAT),
          LONG: parseInt(LONG),
        };
      }
    } catch {
      return null;
    }
  };

  const convertLatLong = (lat: number, long: number) => {
    const longSign = (long >> 31) & 1 ? -1 : 1;
    const longDegrees = (long >> 23) & 0xff;
    const longMinutes = (long >> 17) & 0x3f;
    const longFraction = long & 0x1ffff;
    const longitude =
      longSign * (longDegrees + (longMinutes + longFraction / 100000) / 60);

    const latSign = (lat >> 31) & 1 ? -1 : 1;
    const latDegrees = (lat >> 23) & 0xff;
    const latMinutes = (lat >> 17) & 0x3f;
    const latFraction = lat & 0x1ffff;
    const latitude =
      latSign * (latDegrees + (latMinutes + latFraction / 100000) / 60);

    return { latitude, longitude };
  };

 type LatLng = { latitude: number; longitude: number };

function normalizeLocation(loc: unknown): LatLng {
  const fallback: LatLng = { latitude: 0, longitude: 0 };

  const toNum = (v: unknown) => {
    const n = typeof v === "string" ? Number(v.trim()) : Number(v);
    return Number.isFinite(n) ? n : null;
  };

  // 1) Object form: { latitude, longitude }
  if (loc && typeof loc === "object" && !Array.isArray(loc)) {
    const obj = loc as any;

    if ("latitude" in obj && "longitude" in obj) {
      const lat = toNum(obj.latitude);
      const lon = toNum(obj.longitude);
      if (lat != null && lon != null) return { latitude: lat, longitude: lon };
    }

    // 2) Array-like object with numeric keys: {0: lat, 1: lon}
    if ("0" in obj && "1" in obj) {
      const lat = toNum(obj[0]);
      const lon = toNum(obj[1]);
      if (lat != null && lon != null) return { latitude: lat, longitude: lon };
    }

    return fallback;
  }

  // 3) Array / tuple form: [lat, lon]
  if (Array.isArray(loc)) {
    const lat = toNum(loc[0]);
    const lon = toNum(loc[1]);
    if (lat != null && lon != null) return { latitude: lat, longitude: lon };
    return fallback;
  }

  // 4) String form: "lat, lon" or "lat lon"
  if (typeof loc === "string") {
    const parts = loc.split(/[, ]+/).filter(Boolean);
    const lat = toNum(parts[0]);
    const lon = toNum(parts[1]);
    if (lat != null && lon != null) return { latitude: lat, longitude: lon };
    return fallback;
  }

  return fallback;
}


  type RawArrayResponse = {
    path: string;
    filename: string;
    info: {
      location?: string;
      MAC?: string;
      receiver?: string;
      date?: string;
      [key: string]: any;
    };
    ts: number[];
    data: number[];
  };

  const fetchAndPlotFile = async (path: string) => {
    try {
      const response = await fetch(
        `/portal/api/rawdata/browse/array?path=${encodeURIComponent(path)}`,
        { credentials: 'same-origin' }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const payload = (await response.json()) as RawArrayResponse;

      // ---------- Plot ----------
      setPlotData({
        x: payload.ts,
        y: payload.data,
      });

      // ---------- File info ----------
      const filename = payload.filename || path.split('/').pop() || '';

      // keep your existing filename-based timestamp if backend doesn't supply one
      const dateString = filename.split('_')[0]?.slice(0, 19);
      const timestamp =
        payload.info?.date ??
        (dateString
          ? dateString.replace(/-/g, ':').replace('T', ' ')
          : 'Unknown');

      const location =
        payload.info?.location ?? { latitude: 0, longitude: 0 };

      setFileInfo({
        date: timestamp,
        receiver: payload.info?.MAC ?? payload.info?.receiver ?? 'Unknown',
        location: normalizeLocation(payload.info?.location),
      });
    } catch (err) {
      console.error('Error fetching/plotting file:', err);
    }
  };

  // const fetchAndPlotFile = async (path: string) => {
  //     try {
  //         const response = await fetch(
  //             `/portal/api/croatia/download?path=${encodeURIComponent(path)}`,
  //             { credentials: "same-origin" }
  //         );
  //         if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

  //         const arrayBuffer = await response.arrayBuffer();
  //         const dataView = new DataView(arrayBuffer);

  //         // ✅ Read header length ONCE
  //         const headerLength = dataView.getUint32(0, true);

  //         // ---------- Plot data ----------
  //         const dataStart = 4 + headerLength;
  //         const dataBuffer = new Uint16Array(arrayBuffer.slice(dataStart));
  //         const ADC = Array.from(dataBuffer, val => (val - 2048) / 4);
  //         const ts = ADC.map((_, i) => i / 1e6);

  //         setPlotData({ x: ts, y: ADC });

  //         // ---------- Parse header ----------
  //         const headerBytes = new Uint8Array(arrayBuffer.slice(4, 4 + headerLength));
  //         const headerString = new TextDecoder().decode(headerBytes);

  //         const header = parseHeader(headerString);
  //         if (!header) return;

  //         const location = convertLatLong(header.LAT, header.LONG);

  //         const filename = path.split('/').pop() || '';
  //         const dateString = filename.split('_')[0]?.slice(0, 19);
  //         const timestamp = dateString
  //             ? dateString.replace(/-/g, ':').replace('T', ' ')
  //             : 'Unknown';

  //         setFileInfo({
  //             date: timestamp,
  //             receiver: header.MAC,
  //             location,
  //         });

  //     } catch (err) {
  //         console.error('Error fetching/plotting file:', err);
  //     }
  // };

  const downloadCsv = (file: FileEntry) => {
    const url = `/portal/api/rawdata/browse/array?path=${encodeURIComponent(
      file.path
    )}&download=1`;
    window.open(url, '_blank');
  };

  const renderTree = (entries: FileEntry[], level = 0) =>
    entries.map((entry) => (
      <div key={`${level}-${entry.name}`} style={{ marginLeft: level * 16 }}>
        <div
          className={`file-item ${
            entry.is_dir
              ? 'directory-item'
              : entry.name.endsWith('.bin')
              ? 'bin-file'
              : ''
          }`}
          onClick={() => handleFileClick(entry)}
        >
          {entry.is_dir ? <IoFolderOpen /> : <IoDocumentText />}
          <span style={{width: '100%'}}>{entry.is_dir ? entry.name : entry.name}</span>
        </div>
        {entry.is_dir && loadingDirs.has(entry.path) && (
          <p style={{ marginLeft: 16, fontStyle: 'italic', color: '#888' }}>
            Loading...
          </p>
        )}
        {entry.is_dir &&
          expandedDirs.has(entry.path) &&
          entry.children &&
          entry.children.length > 0 &&
          renderTree(entry.children, level + 1)}
      </div>
    ));

  return (
    <div className="overall-div-for-suite">
      <PortalHeader />

      <h1 className="raw-data-title">Raw Data Management</h1>

      <button
        className="sidebar-toggle"
        onClick={() => setIsSidebarOpen((prev) => !prev)}
      >
        {isSidebarOpen ? 'Hide File Explorer' : 'Show File Explorer'}
      </button>

      <div className="raw-data-section">
        <div
          className={`raw-data-sidebar ${isSidebarOpen ? 'open' : 'collapsed'}`}
        >
          <h2>File Explorer</h2>
          {renderTree(directoryTree)}
        </div>

        <div className="raw-data-content">
          {selectedFile ? (
            <>
              <div className="raw-file-header">
                <h2>{selectedFile.name}</h2>
              </div>

              {(selectedFile.name.endsWith('.bin') ||
                selectedFile.name.endsWith('.csv')) && (
                <>
                  <div className="file-actions">
                    <button
                      className="download-btn"
                      onClick={() => downloadCsv(selectedFile)}
                    >
                      <IoDownload /> Download
                    </button>

                    {selectedFile.name.endsWith('.bin') && (
                      <>
                        <button
                          className="plot-btn"
                          onClick={() => {
                            setIsPlotVisible(true);
                            fetchAndPlotFile(selectedFile.path);
                          }}
                        >
                          <IoBarChart /> Plot File
                        </button>

                        <button className="download-btn" onClick={zoomIn}>
                          <TbZoomIn /> Zoom In
                        </button>

                        <button className="download-btn" onClick={zoomOut}>
                          <TbZoomOut /> Zoom Out
                        </button>

                        <button className="download-btn" onClick={resetZoom}>
                          <TbZoomReset /> Reset Zoom
                        </button>
                      </>
                    )}

                    {/* <label style={{ cursor: 'pointer' }}>
                                            Upload <input type="file" accept=".bin" onChange={e => fetchAndPlotFile(e.target.files![0].name)} style={{ display: 'none' }} />
                                        </label> */}
                  </div>

                  {selectedFile.name.endsWith('.bin') && isPlotVisible && (
                    <div className="plot-placeholder">
                      {plotData ? (
                        <div className="plot-chart-wrapper">
                          <Line
                            ref={chartRef}
                            data={{
                              labels: plotData.x,
                              datasets: [
                                {
                                  label: 'ADC vs Time (s)',
                                  data: plotData.y,
                                  borderColor: '#28aae1',
                                  borderWidth: 1,
                                  pointRadius: 0,
                                },
                              ],
                            }}
                            options={chartOptions}
                          />

                          {isWideScreen && (
                            <p>
                              <strong>Hint:</strong> Make a square with your
                              mouse for a more targeted zoom
                            </p>
                          )}
                        </div>
                      ) : (
                        <p>Plot preview will be generated here</p>
                      )}
                    </div>
                  )}

                  {fileInfo && (
                    <div className="file-metadata">
                      <h3>Header Data</h3>

                      <ul className="raw-list">
                        {/* Remaining fields */}
                        {Object.entries(fileInfo).map(([key, value]) => {
                          if (key === 'GPS Valid') return null;

                          if (key === 'location' && typeof value === 'object') {
                            return (
                              <React.Fragment key="location">
                                <li className="raw-list-item">
                                  <strong className="plot-key">
                                    Latitude:
                                  </strong>
                                  <div className="plot-card-value">
                                    {value.latitude.toFixed(5)}
                                  </div>
                                </li>
                                <li className="raw-list-item">
                                  <strong className="plot-key">
                                    Longitude:
                                  </strong>
                                  <div className="plot-card-value">
                                    {value.longitude.toFixed(5)}
                                  </div>
                                </li>
                              </React.Fragment>
                            );
                          }

                          return (
                            <li key={key} className="raw-list-item">
                              <strong className="plot-key">
                                {key.charAt(0).toUpperCase() + key.slice(1)}:
                              </strong>
                              <div className="plot-card-value">
                                {value?.toString()}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <div className="empty-state">
              <p>Select a file to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RawData;
